/**
 * キャンペーンバナーを閉じる処理をまとめたモジュール。
 * 全プロダクト横断で共通Cookieを使用し、閉じたキャンペーンIDを記録する。
 * Cookie仕様は↓
 * @link https://app.notion.com/p/DAY-DX-36f1b603068580a1b38accce22f906a6#3721b603068580d0a7f6e140a03fda67
 */
import { useState } from 'react'
import { appEnv } from 'src/util/appEnv'
import { isDefined } from 'src/util/isDefined'

const COOKIE_NAME = 'Kmns-Campaign-Dismissed'

const MAX_AGE_SECONDS = 400 * 24 * 60 * 60

/**
 * 値は percent-encode しない。
 * 他プロダクトが生の document.cookie を読む前提のため、カンマを %2C にすると ID が一致しなくなる。
 * campaign_id に Cookie の構文を壊す文字が混ざらないことは parseCampaigns で保証している。
 */
const serialize = (value: string, maxAgeSeconds: number): string =>
  [
    `${COOKIE_NAME}=${value}`,
    'Path=/',
    // 本番のみサブドメインをまたいで共有する。それ以外の環境は自ドメインに閉じる。
    appEnv.isProduction ? 'Domain=kaminashi.com' : undefined,
    'SameSite=Lax',
    // ローカルは http で動くため、Secure を付けるとブラウザが Cookie を保存しない
    appEnv.isLocal ? undefined : 'Secure',
    `Max-Age=${maxAgeSeconds}`,
  ]
    .filter(isDefined)
    .join('; ')

export const getDismissedCampaignIds = (): string[] => {
  const entry = document.cookie
    .split(';')
    .map(cookie => cookie.trim())
    .find(cookie => cookie.startsWith(`${COOKIE_NAME}=`))
  if (entry === undefined) return []

  return entry
    .slice(`${COOKIE_NAME}=`.length)
    .split(',')
    .map(campaignId => campaignId.trim())
    .filter(campaignId => campaignId.length > 0)
}

/**
 * 他プロダクトが書いた campaign_id を消さないよう、既存の値を読んだうえで追記する。
 */
export const addDismissedCampaignId = (campaignId: string): void => {
  const campaignIds = getDismissedCampaignIds()
  if (campaignIds.includes(campaignId)) return

  document.cookie = serialize(
    [...campaignIds, campaignId].join(','),
    MAX_AGE_SECONDS,
  )
}

/**
 * ログアウト時に呼ぶ。Max-Age を 0 にして Cookie ごと消す。
 */
export const removeDismissedCampaignIds = (): void => {
  document.cookie = serialize('', 0)
}

/**
 * このキャンペーンのバナーを閉じたかどうかを、 Cookie をもとに判定する。
 */
export const useCampaignBannerDismissal = (campaignId: string) => {
  const [dismissedCampaignIds, setDismissedCampaignIds] = useState(
    getDismissedCampaignIds,
  )

  const dismiss = () => {
    addDismissedCampaignId(campaignId)
    setDismissedCampaignIds(getDismissedCampaignIds())
  }

  return {
    isDismissed: dismissedCampaignIds.includes(campaignId),
    dismiss,
  }
}
